import {
  prefetchPercent,
  type PrefetchReport,
  type PrefetchTuning,
} from '@vrlearning/lesson-schema';
import { sameAssignment, type Assignment, type Preparation } from '../api/session-client.js';
import type { PrefetchItem } from './plan.js';

/**
 * The headset's prefetch across a session (ADR-038, proposed): what the lobby says while a teacher
 * sets a lesson ready, and which runner runs when. Pure, with the runner and the socket injected,
 * so main.ts only wires it.
 */

/** The lobby's line while a lesson is being prepared; Dutch, for a pupil. */
export function preparingLine(report: PrefetchReport | undefined): string {
  if (!report) return 'Je docent zet een les klaar';
  switch (report.state) {
    case 'ready':
      return 'Klaar. Wacht op je docent.';
    case 'paused':
      return 'Klaarzetten gepauzeerd';
    case 'failed':
      // Nothing for a pupil to do either way: the lesson loads what it needs at the start.
      return report.failed === 'storage'
        ? 'Klaarzetten lukt niet: te weinig opslag'
        : 'Klaarzetten lukt nu niet; de les laadt bij de start';
    default:
      return `Je docent zet een les klaar · ${prefetchPercent(report)} %`;
  }
}

/** A runner as the controller drives it (runner.ts's PrefetchRunner). */
export type PrefetchRun = {
  run(): Promise<PrefetchReport | undefined>;
  stop(): void;
  skip(keys: Iterable<string>): void;
  setParallel(parallel: number): void;
  setMayRun(predicate: () => boolean): void;
  /** Some file was given up on: worth starting over with fresh signatures. */
  readonly hasLeft: boolean;
};

export type PrefetcherParts = {
  /** The lesson's plan (plan.ts's prefetchPlan, with the world's files and this origin). */
  plan: (lesson: Assignment) => PrefetchItem[];
  /** A runner over the plan, reporting to `report`. */
  start: (
    plan: readonly PrefetchItem[],
    tuning: PrefetchTuning,
    report: (report: PrefetchReport) => void,
  ) => PrefetchRun;
  /** `device.prefetch`, to the teacher's grid. */
  send: (report: PrefetchReport) => void;
  /** The lobby's line follows this; undefined when nothing is being prepared. */
  changed?: (report: PrefetchReport | undefined) => void;
};

type Current = {
  lesson: Pick<Assignment, 'sessionId' | 'lesson'>;
  plan: readonly PrefetchItem[];
  run: PrefetchRun;
  /** The runner has returned: ready, failed, or paused for a metered connection. */
  ended: boolean;
  /** Resolves once the runner has returned, whichever way. */
  settled: Promise<void>;
  report: PrefetchReport | undefined;
};

export class Prefetcher {
  readonly #parts: PrefetcherParts;
  #current: Current | undefined;
  #preparing = false;

  constructor(parts: PrefetcherParts) {
    this.#parts = parts;
  }

  /** A lesson is being set ready and not yet started: the lobby holds and says how far. */
  get preparing(): boolean {
    return this.#preparing;
  }

  get report(): PrefetchReport | undefined {
    return this.#current?.report;
  }

  /**
   * `session.prepare`: a runner for this lesson. Said again on every reconnect: the same session and
   * lesson keep the runner they have (its last report said again), unless it gave up, which a
   * reconnect's fresh signatures may mend.
   */
  prepare(preparation: Preparation): void {
    this.#preparing = true;
    if (this.#keeps(preparation)) return;
    this.#begin(preparation, preparation.tuning);
  }

  /**
   * `session.assigned`, once the lesson is built: the start of a prepared lesson keeps its runner
   * behind the lesson -- one request, the current step's files left to the lesson, waiting while
   * `mayRun` says no. Plain Start (nothing prepared) starts one the same way.
   */
  assigned(assignment: Assignment, options: { stepIndex: number; mayRun: () => boolean }): void {
    this.#preparing = false;
    if (!this.#keeps(assignment)) {
      // An older API knows no prefetch: nothing behind its lesson.
      if (!assignment.prefetchOffered) {
        void this.stop();
        return;
      }
      this.#begin(assignment, { ...assignment.tuning, parallel: 1 });
    }
    const current = this.#current!;
    current.run.setParallel(1);
    const step = options.stepIndex + 1;
    // What the lesson fetches itself, never twice: the world and this step's files (the lesson
    // loads them now), the next step's photo (the pano view preloads it) and every voice-over
    // (voice.prefetch fetches them all, after the world).
    const lessonFetches = (item: PrefetchItem): boolean =>
      item.step === 0 ||
      item.step === step ||
      (item.step === step + 1 && item.kind === 'pano') ||
      item.kind === 'voice';
    current.run.skip(current.plan.filter(lessonFetches).map((item) => item.key));
    current.run.setMayRun(options.mayRun);
    this.#parts.changed?.(current.report);
  }

  /**
   * The lesson ended, the student was released, or the headset was unpaired: nothing more.
   * Resolves once the runner has settled, its request aborted: only then may an unpair clear the
   * caches, or a late write would put a school's file back.
   */
  stop(): Promise<void> {
    this.#preparing = false;
    const current = this.#current;
    if (!current) return Promise.resolve();
    current.run.stop();
    this.#current = undefined;
    this.#parts.changed?.(undefined);
    return current.settled;
  }

  /** Whether the runner it has is this lesson's and still worth having; says its report again. */
  #keeps(lesson: Assignment): boolean {
    const current = this.#current;
    if (!current || !sameAssignment(current.lesson, lesson)) return false;
    if (this.#needsRestart(current)) return false;
    if (current.report) this.#parts.send(current.report);
    this.#parts.changed?.(current.report);
    return true;
  }

  /**
   * A runner that gave up on anything is started again: files left after a Wi-Fi blip, or a
   * failure, while still running or not. One that returned without finishing (paused) too.
   */
  #needsRestart(current: Current): boolean {
    if (current.run.hasLeft || current.report?.state === 'failed') return true;
    return current.ended && current.report?.state !== 'ready';
  }

  #begin(lesson: Assignment, tuning: PrefetchTuning): void {
    this.#current?.run.stop();
    const plan = this.#parts.plan(lesson);
    const current: Current = {
      lesson: { sessionId: lesson.sessionId, lesson: lesson.lesson },
      plan,
      ended: false,
      settled: Promise.resolve(),
      report: undefined,
      run: undefined as unknown as PrefetchRun,
    };
    current.run = this.#parts.start(plan, tuning, (report) => {
      // A runner let go of says nothing more.
      if (this.#current !== current) return;
      current.report = report;
      this.#parts.send(report);
      this.#parts.changed?.(report);
    });
    this.#current = current;
    this.#parts.changed?.(undefined);
    current.settled = current.run
      .run()
      .catch(() => undefined)
      .then(() => {
        current.ended = true;
      });
  }
}
